import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, fmtDate, openPodFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Printer, Pencil, Trash2, Search, FileSpreadsheet, Upload, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Consignments() {
  const [items, setItems] = useState([]);
  const [billMap, setBillMap] = useState({});
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);
  const podInputRef = useRef(null);
  const [podCid, setPodCid] = useState(null);

  const triggerPod = (cid) => { setPodCid(cid); setTimeout(() => podInputRef.current?.click(), 0); };
  const onPodFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !podCid) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/consignments/${podCid}/pod`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("POD uploaded & linked");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { if (podInputRef.current) podInputRef.current.value = ""; setPodCid(null); }
  };

  const exportExcel = async () => {
    setBusy("export");
    try {
      const res = await api.get("/consignments/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "consignments_data.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel exported");
    } catch { toast.error("Export failed"); }
    finally { setBusy(""); }
  };

  const importFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy("import");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/consignments/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.created} consignment(s) imported`);
      if (data.errors?.length) toast.warning(`${data.errors.length} row(s) skipped: ${data.errors.slice(0, 2).join("; ")}${data.errors.length > 2 ? "…" : ""}`, { duration: 8000 });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Import failed"); }
    finally { setBusy(""); if (fileRef.current) fileRef.current.value = ""; }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [cnRes, billRes] = await Promise.all([api.get("/consignments"), api.get("/bills")]);
      setItems(cnRes.data);
      const m = {};
      (billRes.data || []).forEach((b) => (b.items || []).forEach((it) => {
        if (it.cn_no && !m[it.cn_no]) m[it.cn_no] = b.bill_no;
      }));
      setBillMap(m);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/consignments/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const STATUS_STYLES = {
    "Booked": "bg-slate-100 text-slate-700",
    "In Transit": "bg-amber-100 text-amber-800",
    "Delivered": "bg-green-100 text-green-700",
  };

  const setStatus = async (c, status) => {
    try {
      await api.put(`/consignments/${c.id}/status`, { status });
      setItems((xs) => xs.map((x) => (x.id === c.id ? { ...x, status } : x)));
      toast.success(`${c.cn_no} marked ${status}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = items.filter((c) =>
    !q || `${c.cn_no} ${c.consignor_name} ${c.consignee_name} ${c.truck_no} ${c.from_location} ${c.to_location}`
      .toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="p-8" data-testid="consignments-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl text-slate-900">Consignment Notes</h1>
          <p className="text-slate-500 text-sm">Lorry Receipts (LR / Bilty)</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button onClick={exportExcel} disabled={!!busy} data-testid="cn-export-btn"
            className="px-3 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2 border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-60">
            {busy === "export" ? <Loader2 size={15} className="animate-spin"/> : <FileSpreadsheet size={15}/>} Export Excel
          </button>
          <button onClick={() => fileRef.current?.click()} disabled={!!busy} data-testid="cn-import-btn"
            className="px-3 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2 border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-60">
            {busy === "import" ? <Loader2 size={15} className="animate-spin"/> : <Upload size={15}/>} Import / Bulk Upload
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.csv" onChange={importFile} className="hidden" data-testid="cn-import-file-input"/>
          <input ref={podInputRef} type="file" accept="image/*,application/pdf" onChange={onPodFile} className="hidden" data-testid="pod-file-input"/>
          <Link to="/portal/consignments/new" className="btn-primary px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2" data-testid="new-consignment-btn">
            <Plus size={16}/> New Consignment
          </Link>
        </div>
      </div>

      <div className="bg-white border border-slate-100 rounded-xl">
        <div className="p-4 border-b border-slate-100 flex items-center gap-2">
          <Search size={16} className="text-slate-400"/>
          <Input placeholder="Search by C/N No, consignor, consignee, truck…" value={q} onChange={(e)=>setQ(e.target.value)} className="border-0 shadow-none focus-visible:ring-0" data-testid="consignments-search"/>
        </div>
        <div className="overflow-auto max-h-[calc(100vh-260px)]">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 sticky top-0 z-10">
              <tr>
                <th className="text-left px-4 py-2">C/N No</th>
                <th className="text-left px-4 py-2">Date</th>
                <th className="text-left px-4 py-2">From → To</th>
                <th className="text-left px-4 py-2">Consignor</th>
                <th className="text-left px-4 py-2">Consignee</th>
                <th className="text-left px-4 py-2">Truck</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Bill No.</th>
                <th className="text-center px-4 py-2">POD</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400">No consignments yet. Create your first one.</td></tr>}
              {filtered.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`consignment-row-${c.cn_no}`}>
                  <td className="px-4 py-2 font-mono text-[#0b5cc4]">{c.cn_no}</td>
                  <td className="px-4 py-2">{fmtDate(c.date)}</td>
                  <td className="px-4 py-2">{c.from_location} → {c.to_location}</td>
                  <td className="px-4 py-2">{c.consignor_name}</td>
                  <td className="px-4 py-2">{c.consignee_name}</td>
                  <td className="px-4 py-2">{c.truck_no}</td>
                  <td className="px-4 py-2">
                    <select
                      value={c.status || "Booked"}
                      onChange={(e) => setStatus(c, e.target.value)}
                      className={`text-xs font-semibold rounded-full px-2.5 py-1 border-0 cursor-pointer appearance-none ${STATUS_STYLES[c.status || "Booked"]}`}
                      data-testid={`status-select-${c.cn_no}`}
                    >
                      <option>Booked</option>
                      <option>In Transit</option>
                      <option>Delivered</option>
                    </select>
                  </td>
                  <td className="px-4 py-2" data-testid={`cn-billno-${c.cn_no}`}>
                    {billMap[c.cn_no]
                      ? <span className="font-mono text-slate-700">{billMap[c.cn_no]}</span>
                      : <span className="text-slate-400 text-xs">Pending</span>}
                  </td>
                  <td className="px-4 py-2">
                    {c.pod_filename ? (
                      <div className="flex justify-center gap-1">
                        <button onClick={()=>openPodFile(c.id, false)} className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1" title="Open POD" data-testid={`pod-open-${c.cn_no}`}><ExternalLink size={13}/> Open</button>
                        <button onClick={()=>openPodFile(c.id, true)} className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1" title="Print POD" data-testid={`pod-print-${c.cn_no}`}><Printer size={13}/> Print</button>
                      </div>
                    ) : (
                      <div className="flex justify-center">
                        <button onClick={()=>triggerPod(c.id)} className="text-xs px-2 py-1 rounded border border-dashed border-slate-300 text-slate-500 hover:bg-slate-50 inline-flex items-center gap-1" title="Upload POD" data-testid={`pod-upload-${c.cn_no}`}><Upload size={13}/> Upload POD</button>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex justify-end gap-1">
                      <Link to={`/portal/consignments/${c.id}/print`} className="p-2 hover:bg-slate-100 rounded" title="Print" data-testid={`print-consignment-${c.cn_no}`}><Printer size={16}/></Link>
                      <Link to={`/portal/consignments/${c.id}/edit`} className="p-2 hover:bg-slate-100 rounded" title="Edit" data-testid={`edit-consignment-${c.cn_no}`}><Pencil size={16}/></Link>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button className="p-2 hover:bg-red-50 rounded text-red-600" title="Delete" data-testid={`delete-consignment-${c.cn_no}`}><Trash2 size={16}/></button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete consignment {c.cn_no}?</AlertDialogTitle>
                            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(c.id)} data-testid="confirm-delete-consignment">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
